import { useState, useEffect, useRef } from 'react';
import photoService from '../services/PhotoService';
import { base64ToFile } from '../../../services/DeliveryDataService';
import { ObjectURLManager, LRUCache } from '../utils/imageOptimizationUtils';

/**
 * 优化后的图片数据结构
 */
export interface OptimizedPhoto {
  id: string;
  thumbnailUrl: string;      // 缩略图 Object URL（用于列表显示）
  originalBase64: string;    // 原始 Base64（用于全屏预览）
  trackingNumber?: string;   // 包裹号（仅 label 类型使用）
}

/**
 * Hook 配置选项
 */
export interface UseOptimizedPhotosOptions {
  enableThumbnail?: boolean;  // 是否启用缩略图（默认 true）
  cacheSize?: number;         // 原图缓存大小（默认 10）
}

/**
 * Hook 返回值
 */
export interface UseOptimizedPhotosResult {
  photos: OptimizedPhoto[];
  isProcessing: boolean;
  error: string | null;
  getFullImageUrl: (id: string) => Promise<string>;
  cleanup: () => void;
}

/**
 * 图片优化 Hook
 * 将 Base64 数组转换为优化后的数据结构（缩略图 Object URL）
 * 
 * @param base64Photos Base64 图片数组
 * @param trackingNumbers 包裹号数组（可选，用于 label 类型）
 * @param options 配置选项
 * @returns 优化后的图片数据和操作方法
 */
export function useOptimizedPhotos(
  base64Photos: string[],
  trackingNumbers?: string[],
  options: UseOptimizedPhotosOptions = {}
): UseOptimizedPhotosResult {
  const {
    enableThumbnail = true,
    cacheSize = 10
  } = options;

  const [photos, setPhotos] = useState<OptimizedPhoto[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // URL 管理器（自动清理）
  const urlManager = useRef(new ObjectURLManager());
  
  // 原图缓存（LRU）
  const fullImageCache = useRef(new LRUCache<string, string>(cacheSize));

  /**
   * 处理 Base64 图片数组，生成缩略图 Object URL
   */
  useEffect(() => {
    if (base64Photos.length === 0) {
      setPhotos([]);
      return;
    }

    let isCancelled = false;
    setIsProcessing(true);
    setError(null);

    const processPhotos = async () => {
      try {
        const optimizedPhotos: OptimizedPhoto[] = [];

        for (let i = 0; i < base64Photos.length; i++) {
          if (isCancelled) break;

          const base64 = base64Photos[i];
          const trackingNumber = trackingNumbers?.[i];

          if (!base64) continue;

          try {
            let thumbnailUrl: string;

            if (enableThumbnail) {
              // 生成缩略图 Object URL
              const file = await base64ToFile(base64, `photo_${i}_${Date.now()}.jpg`);
              const thumbnailFile = await photoService.compressThumbnail(file);
              thumbnailUrl = urlManager.current.createFromFile(thumbnailFile);
            } else {
              // 不使用缩略图，直接使用原图 Object URL
              const file = await base64ToFile(base64, `photo_${i}_${Date.now()}.jpg`);
              thumbnailUrl = urlManager.current.createFromFile(file);
            }

            optimizedPhotos.push({
              id: `photo_${i}_${Date.now()}`,
              thumbnailUrl,
              originalBase64: base64,
              trackingNumber
            });

            // 分批处理：每处理一张图片，延迟一下，避免阻塞 UI
            if (i < base64Photos.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 50));
            }
          } catch (err) {
            console.error(`[useOptimizedPhotos] 处理第 ${i} 张图片失败:`, err);
            // 单张图片失败不影响其他图片
          }
        }

        if (!isCancelled) {
          setPhotos(optimizedPhotos);
          setIsProcessing(false);
        }
      } catch (err) {
        if (!isCancelled) {
          console.error('[useOptimizedPhotos] 批量处理失败:', err);
          setError(err instanceof Error ? err.message : '图片处理失败');
          setIsProcessing(false);
        }
      }
    };

    processPhotos();

    return () => {
      isCancelled = true;
    };
  }, [base64Photos, trackingNumbers, enableThumbnail]);

  /**
   * 按需获取原图 URL（用于全屏预览）
   * 会自动使用 LRU 缓存
   */
  const getFullImageUrl = async (photoId: string): Promise<string> => {
    // 检查缓存
    const cachedUrl = fullImageCache.current.get(photoId);
    if (cachedUrl) {
      return cachedUrl;
    }

    // 找到对应的照片
    const photo = photos.find(p => p.id === photoId);
    if (!photo) {
      throw new Error(`Photo not found: ${photoId}`);
    }

    // 生成原图 Object URL
    const file = await base64ToFile(photo.originalBase64, `full_${photoId}.jpg`);
    const fullImageUrl = urlManager.current.createFromFile(file);

    // 缓存
    fullImageCache.current.set(photoId, fullImageUrl);

    return fullImageUrl;
  };

  /**
   * 手动清理所有 URL（通常在组件卸载时自动调用）
   */
  const cleanup = () => {
    urlManager.current.revokeAll();
    fullImageCache.current.clear();
  };

  // 组件卸载时自动清理
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  return {
    photos,
    isProcessing,
    error,
    getFullImageUrl,
    cleanup
  };
}
